import { useTranslation } from 'react-i18next';
import { Calendar, Truck, PackageCheck } from 'lucide-react';
import { PickupStatus } from '@/types';

const StatusIcons: { [key: number]: React.ReactNode } = {
  0: <Calendar size={20} />,
  1: <Truck size={20} />,
  2: <PackageCheck size={20} />,
};

export interface OrderItemStatus {
  title: string;
  description?: string;
}

interface OrderStatusProps {
  items: OrderItemStatus[];
  statusMessage: string;
  current?: number;
}

const OrderStatus = ({ items, statusMessage, current = 0 }: OrderStatusProps) => {
  const { t } = useTranslation();

  const getStatusColor = (slug: string) => {
    if (slug === PickupStatus.BULK_COLLECTED || slug === 'collected' || slug === 'bulk-collected') {
      return { bg: 'rgba(34,197,94,0.12)', text: 'rgb(22,163,74)' };
    }
    if (slug === 'new' || slug === 'scheduled') {
      return { bg: 'rgba(168,85,247,0.12)', text: 'rgb(147,51,234)' };
    }
    if (slug === 'collecting' || slug === 'out-for-collecting') {
      return { bg: 'rgba(245,158,11,0.12)', text: 'rgb(217,119,6)' };
    }
    return { bg: 'rgba(59,130,246,0.12)', text: 'rgb(37,99,235)' };
  };

  const statusColor = getStatusColor(statusMessage);

  return (
    <div>
      {/* Header: title + status badge */}
      <div className='flex items-center justify-between mb-6'>
        <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
          {String(t('order_status', { defaultValue: 'Orders Status' }))}
        </span>
        <span
          className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold'
          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
        >
          <span className='w-1.5 h-1.5 rounded-full' style={{ backgroundColor: statusColor.text }} />
          {String(t(statusMessage, { defaultValue: statusMessage }))}
        </span>
      </div>

      {/* Pipeline steps */}
      <div className='flex items-center justify-between'>
        {items.map((item, index) => {
          const isCompleted = index <= current;
          const isLast = index === items.length - 1;

          return (
            <div key={index} className='flex items-center flex-1'>
              <div className='flex flex-col items-center gap-2 flex-shrink-0'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center transition-colors'
                  style={{
                    backgroundColor: isCompleted ? 'rgba(237,224,74,0.2)' : 'rgb(245,245,243)',
                    color: isCompleted ? 'rgb(57,57,60)' : 'rgb(115,123,140)',
                  }}
                >
                  {StatusIcons[index]}
                </div>
                <span
                  className='text-[11px] font-medium text-center'
                  style={{ color: isCompleted ? 'rgb(57,57,60)' : 'rgb(115,123,140)' }}
                >
                  {item.title}
                </span>
              </div>
              {!isLast && (
                <div
                  className='flex-1 h-0.5 mx-2 rounded-full mt-[-20px]'
                  style={{
                    backgroundColor: index < current ? 'rgb(237,224,74)' : 'rgb(232,230,227)',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderStatus;
